import React, { useState, useEffect, useRef, forwardRef } from 'react';
import axios from 'axios';
import { Table, Button, Offcanvas, Form, Modal, Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import SetupInfoDetails from './SetupInfoDetails';
import ReactPaginate from 'react-paginate';
import { useLocation, useNavigate } from 'react-router-dom';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  }
});

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
  const token = userInfo?.token;
  const origin = localStorage.getItem('user-origin');
  return {
    Authorization: `Bearer ${token}`,
    'Tenant-Origin': origin
  };
};


const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="btn rounded border d-flex align-items-center justify-content-center shadow-sm bg-white text-muted px-3"
    style={{ height: '38px' }}
    title="Filter by Expiry"
  >
    <i className="mdi mdi-tune font-size-18 me-2"></i> Filter
  </button>
));

const SetupInfoList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [setupInfos, setSetupInfos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSetupData, setSelectedSetupData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState(location.state?.searchKey || '');
  const [expiryFilter, setExpiryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [generatedLicenseContent, setGeneratedLicenseContent] = useState('');
  const [isGeneratingLicense, setIsGeneratingLicense] = useState(false);
  const [autoOpenProcessed, setAutoOpenProcessed] = useState(false);
  const [autoOpenTabState, setAutoOpenTabState] = useState('licenseList');
  const [autoOpenLicenseIdState, setAutoOpenLicenseIdState] = useState(null);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
        fetchSetupInfos(1, searchQuery, expiryFilter, typeFilter);
    }, searchQuery ? 1500 : 0);

    return () => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };
  }, [searchQuery, expiryFilter, typeFilter]);

  useEffect(() => {
    if (location.state?.autoOpenSetup && !autoOpenProcessed && setupInfos.length > 0) {
      // Find the exact setup by key if multiple returned, else just take first
      const matchedSetup = setupInfos.find(s => s.key === location.state.searchKey) || setupInfos[0];
      if (matchedSetup) {
        setSelectedSetupData(matchedSetup);
        setShowDetailsModal(true);
        setAutoOpenProcessed(true);
        setAutoOpenTabState(location.state.autoOpenTab || 'licenseList');
        setAutoOpenLicenseIdState(location.state.autoOpenLicenseId || null);
        
        // Clear state so it doesn't trigger again on reload
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [setupInfos, location.state, autoOpenProcessed, navigate]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleExpiryFilterChange = (e) => {
    setExpiryFilter(e.target.value);
  };
  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
  };

  const fetchSetupInfos = async (page = 1, search = searchQuery, filter = expiryFilter, type = typeFilter) => {
    try {
      const params = { page, search };
      if (filter) {
        params.expiry_filter = filter;
      }
      if (type) {
        params.setup_type = type;
      }
      const response = await axios.get(`${backendUrl}/api/v360-setup-info`, { params, headers: getHeaders() });
      const resData = response.data;
      if (resData && resData.data) {
        setSetupInfos(resData.data);
        setCurrentPage((resData.current_page || 1) - 1);
        setPageCount(resData.last_page || 1);
      } else {
        // Fallback for unexpected format
        setSetupInfos(Array.isArray(resData) ? resData : []);
      }
    } catch (error) {
      console.error('Error fetching setup infos', error);
      Toast.fire({ icon: 'error', title: 'Failed to load setup infos' });
    }
  };

  const handleShowModal = (record = null) => {
    setCurrentRecord(record);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setCurrentRecord(null);
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    const numericFields = ['max_tenants', 'max_process'];
    numericFields.forEach(field => {
      if (data[field] !== undefined) {
        if (data[field] === "") {
          data[field] = 0;
        } else {
          data[field] = Number(data[field]);
        }
      }
    });

    data.ip_check = formData.get('ip_check') === 'on';
    data.ip_list = data.ip_list ? data.ip_list.split(',').map(ip => ip.trim()).filter(Boolean) : [];

    try {
      if (currentRecord && currentRecord.id) {
        await axios.patch(`${backendUrl}/api/v360-setup-info/${currentRecord.id}`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record updated successfully' });
      } else {
        await axios.post(`${backendUrl}/api/v360-setup-info`, data, { headers: getHeaders() });
        Toast.fire({ icon: 'success', title: 'Record created successfully' });
      }
      handleCloseModal();
      fetchSetupInfos(currentPage + 1);
    } catch (error) {
      console.error('Error saving record', error);
      Toast.fire({ icon: 'error', title: 'Failed to save record' });
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${backendUrl}/api/v360-setup-info/${id}`, { headers: getHeaders() });
          Swal.fire('Deleted!', 'Record has been deleted.', 'success');
          fetchSetupInfos(currentPage + 1);
        } catch (error) {
          console.error('Error deleting record', error);
          Swal.fire('Error!', 'Failed to delete record.', 'error');
        }
      }
    });
  };

  const handleGenerateLicense = async (info) => {
    setIsGeneratingLicense(true);
    setGeneratedLicenseContent('');
    setShowLicenseModal(true);

    try {
      // Call backend to generate license using stored setup info
        const payload = {
        "fingerprint": info.key,
        "instance_name": info.client_name,
        "instance_id": info.client_id
      };

      // const response = await axios.post('https://control.systech.ae:1880/api/license/get', payload);
      const response = await axios.post(`${backendUrl}/api/v360-setup-info/${info.id}/generate-license`, {}, { headers: getHeaders() });
      let content = '';
      if (response.data && response.data.license) {
          content = response.data.license;
      } else if (typeof response.data === 'string') {
          content = response.data;
      } else {
          content = JSON.stringify(response.data, null, 2);
      }
      // Ensure any literal escaped newlines are converted to actual newlines for proper display
      content = content.replace(/\\n/g, '\n').trim();
      setGeneratedLicenseContent(content);
    } catch (error) {
      console.error('Error generating license:', error);
      setGeneratedLicenseContent('Failed to generate license. Please try again.');
      Toast.fire({ icon: 'error', title: 'Failed to generate license' });
    } finally {
      setIsGeneratingLicense(false);
    }
  };

  const handleCopyLicense = () => {
    navigator.clipboard.writeText(generatedLicenseContent);
    Toast.fire({ icon: 'success', title: 'Certificate copied to clipboard' });
  };
  const handleResetSync = async (id) => {
  try {
    await axios.patch(`${backendUrl}/api/v360-setup-info/${id}/reset-sync`, {}, { headers: getHeaders() });
    setSetupInfos((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, sync_required: false } : item
      )
    );
    Toast.fire({ icon: 'success', title: 'Marked as synced' });
  } catch (error) {
    console.error('Error resetting sync flag', error);
    Toast.fire({ icon: 'error', title: 'Failed to reset sync flag' });
  }
};

  const handleDownloadLicense = () => {
    const blob = new Blob([generatedLicenseContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'license_certificate.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper to clean up the generated license string
  const formatLicenseContent = (content: string) => {
    // Remove curly braces and quotes
    let cleaned = content.replace(/[{}]/g, '').replace(/"/g, '');
    // Convert escaped newline sequences to real newlines
    cleaned = cleaned.replace(/\\n/g, '\n');
    // Replace commas with newline for readability
    cleaned = cleaned.replace(/,/g, '\n');
    // Split into lines, trim, and remove empty lines
    return cleaned
      .split('\n')
      .map(line => line.trim())
      .filter(line => line)
      .join('\n');
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row w-100">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">V360 Setup Info</h4>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <div className="d-flex">
                      <div className="search-box me-2 mb-2 d-inline-block">
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search setup info..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                          <i className="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">

                      <Dropdown>
                        <Dropdown.Toggle as={CustomToggle} />
                        <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                          <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Type</Dropdown.Header>
                          <Dropdown.Item active={typeFilter === ''} onClick={() => handleTypeFilterChange({target: {value: ''}})}>All Types</Dropdown.Item>
                          <Dropdown.Item active={typeFilter === 'production'} onClick={() => handleTypeFilterChange({target: {value: 'production'}})}>Production</Dropdown.Item>
                          <Dropdown.Item active={typeFilter === 'testing'} onClick={() => handleTypeFilterChange({target: {value: 'testing'}})}>Testing</Dropdown.Item>
                          <Dropdown.Item active={typeFilter === 'development'} onClick={() => handleTypeFilterChange({target: {value: 'development'}})}>Development</Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Expiry</Dropdown.Header>
                          <Dropdown.Item active={expiryFilter === ''} onClick={() => handleExpiryFilterChange({target: {value: ''}})}>All Setups</Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === 'expired'} onClick={() => handleExpiryFilterChange({target: {value: 'expired'}})}>
                            <span className="text-danger fw-bold"><i className="mdi mdi-alert-circle-outline me-1"></i>Expired</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === 'expired_7_days'} onClick={() => handleExpiryFilterChange({target: {value: 'expired_7_days'}})}>
                            <span className="text-danger"><i className="mdi mdi-clock-alert-outline me-1"></i>Expired in 7 days</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === '1_day'} onClick={() => handleExpiryFilterChange({target: {value: '1_day'}})}>
                            <span className="text-warning"><i className="mdi mdi-timer-sand me-1"></i>Expiring in 1 Day</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === '7_days'} onClick={() => handleExpiryFilterChange({target: {value: '7_days'}})}>
                            <span className="text-warning"><i className="mdi mdi-calendar-alert me-1"></i>Expiring in 7 Days</span>
                          </Dropdown.Item>
                          <Dropdown.Item active={expiryFilter === '30_days'} onClick={() => handleExpiryFilterChange({target: {value: '30_days'}})}>
                            <span className="text-info"><i className="mdi mdi-calendar-clock me-1"></i>Expiring in 30 Days</span>
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={() => { handleTypeFilterChange({target: {value: ''}}); handleExpiryFilterChange({target: {value: ''}}); }}>
                            <span className="text-secondary fw-bold"><i className="mdi mdi-refresh me-1"></i>Reset All Filters</span>
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    <button
                      type="button"
                      className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                      onClick={() => handleShowModal()}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Add Setup Info"
                    >
                      <i className="mdi mdi-plus"></i>
                    </button>
                  </div>
                </div>
                <div>
                    <table className="table align-middle table-nowrap table-check table-list">
                      <thead className="table-sales-gradient">
                        <tr>
                          <th className="align-middle">ID</th>
                          {/* <th className="align-middle">Key</th> */}
                          <th className="align-middle">
                            <div className="d-flex justify-content-center">
                              <div style={{ width: '150px', textAlign: 'left' }}>
                                Setup Name
                              </div>
                            </div>
                          </th>
                          {/* <th className="align-middle">Client ID</th> */}
                          <th className="align-middle">Setup Mode</th>
                            <th className="align-middle">Setup Type</th>
                            <th className="align-middle">Max Tenants</th>
                          <th className="align-middle">Max Process</th>
                          <th className="align-middle">IP Check</th>
                          {/* <th className="align-middle">IP List</th> */}

                            <th className="align-middle">No of Tenants</th>
                            <th className="align-middle">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {setupInfos.map((info, index) => (
                          <tr key={info.id}>
                            {/* <td>{(currentPage * 15) + index + 1}</td> */}
                            <td>
  <span
    style={{
      color: info.sync_required ? '#dc3545' : 'inherit',
      fontWeight: info.sync_required ? 600 : 'normal',
      cursor: info.sync_required ? 'pointer' : 'default',
    }}
    title={info.sync_required ? 'Pending sync — click to mark as synced' : ''}
    onClick={() => info.sync_required && handleResetSync(info.id)}
  >
    {(currentPage * 15) + index + 1}
    {info.sync_required && (
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#dc3545',
          marginLeft: 6,
        }}
      ></span>
    )}
  </span>
</td>
                            {/* <td 
                              style={{ cursor: 'pointer' }} 
                              onClick={() => { setSelectedSetupData(info); setShowDetailsModal(true); }}
                            >
                              {info.key}
                            </td> */}
                            <td 
                              style={{ cursor: 'pointer' }} 
                              onClick={() => { setSelectedSetupData(info); setShowDetailsModal(true); }}
                            >
                              <div className="d-flex justify-content-center">
                                <div style={{ width: '150px', textAlign: 'left' }}>
                                  {info.client_name}
                                </div>
                              </div>
                            </td>
                            {/* <td>{info.client_id}</td> */}
                            <td className="text-capitalize">{info.setup_mode}</td>
                              <td className="text-capitalize">{info.setup_type || '-'}</td>
                              <td>{info.max_tenants}</td>
                            <td>{info.max_process}</td>
                            <td>{info.ip_check ? 'Yes' : 'No'}</td>
                            <td>{info.tenantCount ?? ''}</td>
                            {/* <td>{info.ip_list ? info.ip_list.join(', ') : ''}</td> */}

                            <td>
                              <div className="dropdown">
                                <i
                                  className="mdi mdi-dots-horizontal font-size-18"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                  style={{ cursor: 'pointer' }}
                                ></i>
                                <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                  <li>
                                    <button className="dropdown-item text-primary" onClick={() => { setSelectedSetupData(info); setShowDetailsModal(true); }}>View Details</button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item text-success" onClick={() => handleGenerateLicense(info)}>Generate License</button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item" onClick={() => handleShowModal(info)}>Edit</button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item text-danger" onClick={() => handleDelete(info.id)}>Delete</button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {pageCount > 1 && (
                    <div className="d-flex justify-content-end mt-3">
                      <ReactPaginate
                        previousLabel={"<"}
                        nextLabel={">"}
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={3}
                        onPageChange={(data) => fetchSetupInfos(data.selected + 1)}
                        forcePage={currentPage}
                        containerClassName={"pagination pagination-rounded"}
                        pageClassName={"page-item"}
                        pageLinkClassName={"page-link"}
                        previousClassName={"page-item"}
                        previousLinkClassName={"page-link"}
                        nextClassName={"page-item"}
                        nextLinkClassName={"page-link"}
                        breakClassName={"page-item"}
                        breakLinkClassName={"page-link"}
                        activeClassName={"active"}
                      />
                    </div>
                  )}

                  <Offcanvas show={showModal} onHide={handleCloseModal} placement="end" style={{ width: '500px' }}>
                    <Offcanvas.Header closeButton>
                      <Offcanvas.Title className="fw-bold text-primary">{currentRecord ? 'Edit Setup Info' : 'Add Setup Info'}</Offcanvas.Title>
                    </Offcanvas.Header>
                    <hr style={{ margin: 0 }} />
                    <Offcanvas.Body>
                      <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">Key</Form.Label>
                          <Form.Control className="form-feild" type="text" name="key" defaultValue={currentRecord?.key || ''} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">Setup Name</Form.Label>
                          <Form.Control className="form-feild" type="text" name="client_name" defaultValue={currentRecord?.client_name || ''} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">Client ID</Form.Label>
                          <Form.Control className="form-feild" type="text" name="client_id" defaultValue={currentRecord?.client_id || ''} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">Setup Mode</Form.Label>
                          <Form.Select className="form-feild" name="setup_mode" defaultValue={currentRecord?.setup_mode || ''}>
                            <option value="">Select Type</option>
                            <option value="single">Single</option>
                            <option value="multi">Multi</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">Setup Type</Form.Label>
                          <Form.Select className="form-feild" name="setup_type" defaultValue={currentRecord?.setup_type || ''}>
                                                        <option value="">Select Type</option>

                            <option value="production">Production</option>
                            <option value="testing">Testing</option>
                            <option value="development">Development</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">Max Tenants</Form.Label>
                          <Form.Control className="form-feild" type="number" name="max_tenants" defaultValue={currentRecord?.max_tenants || ''} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">Max Process</Form.Label>
                          <Form.Control type="number" name="max_process" defaultValue={currentRecord?.max_process || ''} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Check type="checkbox" label={<span className="form-label form-font">Enable IP Check</span>} name="ip_check" defaultChecked={currentRecord?.ip_check || false} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="form-label form-font">IP List </Form.Label>
                          <Form.Control type="text" name="ip_list" defaultValue={currentRecord?.ip_list ? currentRecord.ip_list.join(', ') : ''} />
                        </Form.Group>
                        <div className="d-flex justify-content-end mt-auto mb-4">
                          <Button variant="secondary" onClick={handleCloseModal} className="me-2">Cancel</Button>
                          <Button variant="primary" type="submit">Save</Button>
                        </div>
                      </Form>
        
                  </Offcanvas.Body>
            </Offcanvas>

        {selectedSetupData && (
        <SetupInfoDetails
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          setupData={selectedSetupData}
          onEditSetup={(setup) => {
             setShowDetailsModal(false);
             handleShowModal(setup);
          }}
          activeExpiryFilter={location.state?.activeExpiryFilter || expiryFilter}
          defaultTab={autoOpenTabState}
          autoOpenLicenseId={autoOpenLicenseIdState}
          onSetupUpdated={() => fetchSetupInfos(currentPage)}
        />
      )}

  <Offcanvas
  show={showLicenseModal}
  onHide={() => setShowLicenseModal(false)}
  placement="end"
  style={{ width: '650px' }}
>
  <Offcanvas.Header closeButton>
    <Offcanvas.Title className="fw-bold text-primary">
      License Certificate
    </Offcanvas.Title>
  </Offcanvas.Header>
  <hr style={{ margin: 0 }} />
  {/* Body is now just a flex column that fills available height - no overflow here */}
  <Offcanvas.Body
    className="d-flex flex-column p-0"
    style={{ height: '100%', overflow: 'hidden' }}
  >
    {/* Scrollable content region - takes remaining space, scrolls independently */}
    <div
      className="flex-grow-1"
      style={{ overflowY: 'auto', padding: '1rem', minHeight: 0 }}
    >
      {isGeneratingLicense ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="mt-2">Generating License…</p>
        </div>
      ) : (
        <pre
          className="bg-light p-3 rounded"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {generatedLicenseContent}
        </pre>
      )}
    </div>

    {/* Fixed footer - always visible, never scrolls away */}
    <div className="d-flex justify-content-end p-3 border-top" style={{ flexShrink: 0 }}>
      <Button
        variant="secondary"
        onClick={() => setShowLicenseModal(false)}
        className="me-2"
      >
        Close
      </Button>
      {generatedLicenseContent && generatedLicenseContent.trim() && (
        <>
          <Button
            variant="outline-primary"
            onClick={handleCopyLicense}
            className="me-2"
          >
            Copy
          </Button>
          <Button variant="primary" onClick={handleDownloadLicense}>
            Download
          </Button>
        </>
      )}
    </div>
  </Offcanvas.Body>
</Offcanvas>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupInfoList;
